import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectField,
  SelectInput,
  TextField,
  TextInput,
  useTranslate,
} from 'react-admin';
import { CommandCategoryIcon, CommandIconField, DangerLevelBadge } from '../../components/commands';
import { Command } from '../../types/commands';
import { CommandRestoreButton } from './CommandRestoreButton';

const CommandFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="q" alwaysOn />
    <SelectInput
      source="category"
      choices={[
        { id: 'system', name: 'System' },
        { id: 'application', name: 'Application' },
        { id: 'network', name: 'Network' },
        { id: 'diagnostics', name: 'Diagnostics' },
      ]}
    />
    <SelectInput
      source="danger_level"
      choices={[
        { id: 'low', name: 'Low' },
        { id: 'medium', name: 'Medium' },
        { id: 'high', name: 'High' },
        { id: 'critical', name: 'Critical' },
      ]}
    />
    <BooleanInput source="is_active" label="Active Only" />
  </Filter>
);

/**
 * Deleted commands stay in the list, de-emphasised through the theme's disabled
 * tokens rather than a fixed grey, and keep their row click so they can be
 * opened and restored (TVR-1580).
 */
const rowSx = (record: Command) =>
  record?.deleted_at
    ? {
        'backgroundColor': 'action.hover',
        '& .MuiTableCell-root': { color: 'text.disabled' },
        '& .MuiTableCell-root .MuiSvgIcon-root': { color: 'text.disabled' },
      }
    : {};

const DeletedStatusField = () => {
  const translate = useTranslate();

  return (
    <FunctionField
      label="resources.commands.fields.status"
      render={(record: Command) =>
        record.deleted_at ? (
          <Tooltip title={translate('resources.commands.status.deleted_at', { date: record.deleted_at })}>
            <Chip
              icon={<DeleteOutlineIcon />}
              label={translate('resources.commands.status.deleted')}
              size="small"
              color="default"
              variant="outlined"
            />
          </Tooltip>
        ) : (
          <Chip label={translate('resources.commands.status.active')} size="small" color="success" variant="outlined" />
        )
      }
    />
  );
};

export const CommandList = () => (
  <List
    filters={<CommandFilter />}
    sort={{ field: 'name', order: 'ASC' }}
    perPage={25}
    // `with_trashed` is a plain query param on `GET /v3/api/commands`, so it goes
    // through `meta` instead of the `filter[...]`-prefixed list filters.
    queryOptions={{ meta: { with_trashed: true } }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false} rowSx={rowSx}>
      <FunctionField
        label="Icon"
        render={(record: Command) => (record.icon ? <CommandIconField icon={record.icon} size="small" /> : null)}
      />
      <FunctionField
        label="Category"
        render={(record: Command) =>
          record.category ? <CommandCategoryIcon category={record.category} size="small" /> : null
        }
      />
      <TextField source="name" />
      <TextField source="description" />
      <SelectField
        source="category"
        choices={[
          { id: 'system', name: 'System' },
          { id: 'application', name: 'Application' },
          { id: 'network', name: 'Network' },
          { id: 'diagnostics', name: 'Diagnostics' },
        ]}
      />
      <FunctionField
        label="Danger Level"
        render={(record: Command) =>
          record.danger_level ? <DangerLevelBadge level={record.danger_level} size="small" /> : null
        }
      />
      <BooleanField source="is_active" />
      <BooleanField source="confirmation_required" />
      <DeletedStatusField />
      <CommandRestoreButton stopPropagation />
    </Datagrid>
  </List>
);
